import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Batch payment runs — FR-ACC-04.07
 * Build a draft run of open invoices, approve it, execute it.
 * Phase B styling.
 */
export function PaymentRunsPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const listQuery = useQuery({ queryKey: ['ap', 'runs'], queryFn: () => accountingApi.listPaymentRuns() });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = listQuery.data ?? [];
    let draft = 0, approved = 0, executed = 0, totalPending = 0;
    for (const r of rows) {
      if (r.status === 'draft') draft++;
      else if (r.status === 'approved') { approved++; totalPending += parseFloat(r.total_amount) || 0; }
      else if (r.status === 'executed') executed++;
    }
    const filtered = statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows;
    return { kpis: { total: rows.length, draft, approved, executed, totalPending }, filteredRows: filtered };
  }, [listQuery.data, statusFilter]);

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'approved',  label: 'Approved' },
    { value: 'executed',  label: 'Executed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Payables"
        title="Payment runs"
        subtitle="Select open supplier invoices, approve as a batch, and execute payments in one pass."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New run
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total runs" value={kpis.total} />
        <KpiTile label="Draft" value={kpis.draft}
          accent={kpis.draft > 0 ? 'amber' : 'default'} />
        <KpiTile label="Awaiting execution" value={kpis.approved}
          accent={kpis.approved > 0 ? 'amber' : 'default'}
          sublabel={kpis.totalPending > 0 ? `${fmtCurrency(kpis.totalPending)} queued` : undefined} />
        <KpiTile label="Executed" value={kpis.executed} accent="finance" />
      </div>

      {/* Status chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto border-slate-200">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_32rem] gap-6">
        <div className="card overflow-hidden border-slate-200">
          {listQuery.isLoading ? (
            <LoadingState rows={5} />
          ) : listQuery.error ? (
            <ErrorState error={listQuery.error} />
          ) : !filteredRows.length ? (
            <EmptyState
              message={statusFilter ? `No ${statusFilter} runs` : 'No payment runs yet'}
              hint={statusFilter ? 'Try a different filter.' : 'Create a run to pay open vendor invoices in a batch.'}
              action={!statusFilter && (
                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                  + New run
                </button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Run #</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Method</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Payments</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRows.map((r: any, i: number) => (
                    <tr key={r.id} onClick={() => setSelected(r.id)}
                        className={clsx(
                          'cursor-pointer transition-colors',
                          selected === r.id
                            ? 'bg-finance-50'
                            : i % 2 ? 'bg-slate-50/40 hover:bg-slate-50' : 'bg-white hover:bg-slate-50'
                        )}>
                      <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{r.run_number}</td>
                      <td className="px-4 py-2">
                        <span className="font-mono text-2xs font-semibold uppercase tracking-wider text-slate-700">
                          {r.method}
                        </span>
                      </td>
                      <td className="px-4 py-2"><RunStatusBadge status={r.status} /></td>
                      <td className="px-4 py-2 text-right font-mono text-sm text-slate-700 tabular-nums">{r.payment_count}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                        ${parseFloat(r.total_amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px] border-slate-200">
          {!selected ? (
            <EmptyState
              message="Select a run"
              hint="Click a row on the left to view items and lifecycle actions."
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <line x1="2" y1="10" x2="22" y2="10"/>
                </svg>
              }
            />
          ) : (
            <RunDetail runId={selected} onChanged={() => qc.invalidateQueries({ queryKey: ['ap'] })} />
          )}
        </div>
      </div>

      {showCreate && <CreateRunModal onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['ap', 'runs'] });
          setShowCreate(false); setSelected(id);
        }} />}
    </div>
  );
}

function RunStatusBadge({ status }: { status: string }) {
  const cls = {
    draft:     'bg-slate-100 text-slate-700 ring-slate-200',
    approved:  'bg-amber-50 text-amber-800 ring-amber-200',
    executed:  'bg-sage-50 text-sage-700 ring-sage-200',
    cancelled: 'bg-red-50 text-red-700 ring-red-200',
  }[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {status}
    </span>
  );
}

// ============================================================
// RunDetail
// ============================================================
function RunDetail({ runId, onChanged }: { runId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showCancel, setShowCancel] = useState(false);
  const [showExecute, setShowExecute] = useState(false);

  const q = useQuery({ queryKey: ['ap', 'run', runId], queryFn: () => accountingApi.getPaymentRun(runId) });

  const approveMut = useMutation({
    mutationFn: () => accountingApi.approvePaymentRun(runId),
    onSuccess: () => { toast.success('Approved'); qc.invalidateQueries({ queryKey: ['ap'] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const executeMut = useMutation({
    mutationFn: () => accountingApi.executePaymentRun(runId),
    onSuccess: (d) => {
      toast.success(`Executed: ${d.paidCount} paid${d.failedCount ? ` · ${d.failedCount} failed` : ''}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['ap'] });
      onChanged();
      setShowExecute(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cancelMut = useMutation({
    mutationFn: (reason: string) => accountingApi.cancelPaymentRun(runId, reason),
    onSuccess: () => { toast.success('Cancelled'); qc.invalidateQueries({ queryKey: ['ap'] }); onChanged(); setShowCancel(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const removeMut = useMutation({
    mutationFn: (itemId: string) => accountingApi.removeRunItem(runId, itemId),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['ap', 'run', runId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const r = q.data;

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-finance-700 mb-1">Payment run</div>
            <div className="font-mono font-bold text-lg text-slate-900">{r.run_number}</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-mono text-2xs font-semibold uppercase tracking-wider text-finance-700 bg-finance-50 ring-1 ring-inset ring-finance-200 px-2 py-0.5 rounded">
                {r.method}
              </span>
              <span className="font-mono text-xs text-slate-500">
                {new Date(r.run_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
          <RunStatusBadge status={r.status} />
        </div>
      </div>

      {/* Summary tile */}
      <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="eyebrow text-slate-500">Payments</div>
            <div className="font-mono font-bold text-2xl text-slate-900 tabular-nums mt-1">
              {r.payment_count}
            </div>
          </div>
          <div className="text-right">
            <div className="eyebrow text-slate-500">Total amount</div>
            <div className="font-display font-bold text-2xl text-slate-900 tabular-nums tracking-tight mt-0.5">
              ${parseFloat(r.total_amount).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-slate-50 rounded-xl p-3 mb-4 space-y-1.5 text-xs text-slate-600 border border-slate-200">
        <div>
          <span className="text-slate-400">Created:</span>{' '}
          {new Date(r.created_at).toLocaleString()} · {r.created_by_name}
        </div>
        {r.approved_at && (
          <div>
            <span className="text-slate-400">Approved:</span>{' '}
            {new Date(r.approved_at).toLocaleString()} · {r.approved_by_name}
          </div>
        )}
        {r.executed_at && (
          <div className="text-sage-700">
            <span className="text-sage-600">Executed:</span>{' '}
            {new Date(r.executed_at).toLocaleString()} · {r.executed_by_name}
          </div>
        )}
        {r.cancelled_at && (
          <div className="text-danger">
            <span className="text-red-600">Cancelled:</span> {r.cancellation_reason}
          </div>
        )}
      </div>

      {/* Line items */}
      <div className="eyebrow text-slate-500 mb-2">Invoices ({r.items.length})</div>
      <div className="rounded-lg border border-slate-200 overflow-hidden max-h-80 overflow-y-auto mb-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Supplier / invoice</th>
              <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
              <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Amount</th>
              {r.status === 'draft' && <th className="px-2 py-2 w-8"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {r.items.map((it: any) => (
              <tr key={it.id} className="hover:bg-slate-50/60">
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{it.supplier_name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-mono text-2xs text-slate-600">{it.invoice_number}</span>
                    <span className="text-2xs text-slate-400">·</span>
                    <span className="text-2xs text-slate-500">
                      due {new Date(it.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <ItemStatus status={it.status} errorMessage={it.error_message} paymentNumber={it.payment_number} />
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                  ${parseFloat(it.amount).toFixed(2)}
                </td>
                {r.status === 'draft' && (
                  <td className="px-2 py-2">
                    <button
                      className="text-danger hover:bg-red-50 w-6 h-6 rounded flex items-center justify-center transition-colors text-xs"
                      onClick={() => removeMut.mutate(it.id)}
                      aria-label="Remove invoice from run"
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {r.status === 'draft' && (
          <>
            <button
              className="text-xs font-semibold text-danger hover:bg-red-50 px-3 py-1.5 rounded-pill transition-colors"
              onClick={() => setShowCancel(true)}
            >
              Cancel run
            </button>
            <button
              className="btn-primary"
              disabled={approveMut.isPending}
              onClick={() => approveMut.mutate()}
            >
              {approveMut.isPending ? 'Approving…' : 'Approve run'}
            </button>
          </>
        )}
        {r.status === 'approved' && (
          <>
            <button
              className="text-xs font-semibold text-danger hover:bg-red-50 px-3 py-1.5 rounded-pill transition-colors"
              onClick={() => setShowCancel(true)}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              disabled={executeMut.isPending}
              onClick={() => setShowExecute(true)}
            >
              Execute payments
            </button>
          </>
        )}
      </div>

      {showCancel && (
        <CancelDialog
          loading={cancelMut.isPending}
          onClose={() => setShowCancel(false)}
          onConfirm={(reason) => cancelMut.mutate(reason)}
        />
      )}

      {showExecute && (
        <ExecuteDialog
          paymentCount={r.payment_count}
          totalAmount={parseFloat(r.total_amount)}
          loading={executeMut.isPending}
          onClose={() => setShowExecute(false)}
          onConfirm={() => executeMut.mutate()}
        />
      )}
    </div>
  );
}

function ItemStatus({ status, errorMessage, paymentNumber }: { status: string; errorMessage?: string; paymentNumber?: string }) {
  const tone =
    status === 'paid'   ? 'bg-sage-50 text-sage-700 ring-sage-200' :
    status === 'failed' ? 'bg-red-50 text-red-700 ring-red-200' :
                          'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <div>
      <span
        className={clsx(
          'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
          tone
        )}
        title={errorMessage}
      >
        {status}
      </span>
      {paymentNumber && (
        <div className="font-mono text-2xs text-slate-500 mt-0.5">{paymentNumber}</div>
      )}
    </div>
  );
}

// ============================================================
// Modals
// ============================================================
function CancelDialog({
  loading, onClose, onConfirm,
}: { loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Cancel · Payables</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Cancel payment run</h2>
        <p className="text-sm text-slate-500 mt-2">
          This voids the run. Invoices stay unpaid and become eligible for a future run.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Duplicate run — superseded by PR-2026-003"
        />
      </div>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep run</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason)}
        >
          {loading ? 'Cancelling…' : 'Cancel run'}
        </button>
      </div>
    </Modal>
  );
}

function ExecuteDialog({
  paymentCount, totalAmount, loading, onClose, onConfirm,
}: {
  paymentCount: number;
  totalAmount: number;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-finance-700 mb-1">Execute · Payables</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Execute payment run</h2>
        <p className="text-sm text-slate-500 mt-2">
          This sends payments to suppliers and posts the accounting entries. It cannot be reversed — only corrected via manual adjustments.
        </p>
      </div>
      <div className="px-6 py-5">
        <div className="bg-finance-50 border border-finance-200 rounded-xl p-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="eyebrow text-finance-700">Payments</div>
              <div className="font-mono font-bold text-2xl text-slate-900 tabular-nums mt-1">
                {paymentCount}
              </div>
            </div>
            <div>
              <div className="eyebrow text-finance-700">Total</div>
              <div className="font-display font-bold text-2xl text-slate-900 tabular-nums tracking-tight mt-0.5">
                ${totalAmount.toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Not yet</button>
        <button
          className="btn-primary"
          disabled={loading}
          onClick={onConfirm}
        >
          {loading ? 'Executing…' : 'Execute payments'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// CreateRunModal
// ============================================================
function CreateRunModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [method, setMethod] = useState<'ach' | 'wire' | 'check'>('ach');
  const [dueBy, setDueBy] = useState((() => {
    const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10);
  })());
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => accountingApi.createPaymentRun({
      method, dueBy: dueBy || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (d) => {
      toast.success(`Run ${d.runNumber} · ${d.paymentCount} invoices · ${fmtCurrency(d.totalAmount)}`, { duration: 6000 });
      onCreated(d.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-finance-700 mb-1">Accounting · Payables</div>
        <h2 className="text-xl font-bold font-display text-slate-900">New payment run</h2>
        <p className="text-sm text-slate-500 mt-2">
          Creates a draft run auto-populated with open matched invoices due by the selected date. You can prune before approving.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Payment method</label>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value as any)}>
            <option value="ach">ACH</option>
            <option value="wire">Wire</option>
            <option value="check">Check</option>
          </select>
        </div>
        <div>
          <label className="label">Include invoices due on or before</label>
          <input type="date" className="input" value={dueBy} onChange={(e) => setDueBy(e.target.value)} />
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-slate-400 normal-case tracking-normal">(optional)</span></label>
          <input
            className="input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Bi-weekly AP cycle, quarter-close, etc."
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create draft'}
        </button>
      </div>
    </Modal>
  );
}

function KpiTile({
  label, value, accent = 'default', sublabel,
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
  sublabel?: string;
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-2xs text-slate-500 mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
